import React, { FC, useMemo } from "react";
import { Pressable, StyleProp, View, ViewStyle } from "react-native";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Country } from "@lookiero/sty-psp-locale";
import { Text } from "@lookiero/sty-psp-ui";
import { Icon, LazyImage } from "@lookiero/sty-psp-ui";
import { CheckoutItemStatus } from "../../../../../../domain/checkoutItem/model/checkoutItem";
import { ColorProjection, MediaProjection } from "../../../../../../projection/checkoutItem/checkoutItem";
import { PriceProjection } from "../../../../../../projection/price/price";
import { size, SizeProjection } from "../../../../../../projection/size/size";
import { Price } from "../../../../components/atoms/price/Price";
import { useMediaImage } from "../../../../hooks/useMediaImage";
import { COLOR_I18N_PREFIX, DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { IMAGE_WIDTH, style as productVariantStyle } from "./ProductVariant.style";

interface ProductVariantStyle {
  readonly image: StyleProp<ViewStyle>;
}

interface ProductVariantProps {
  readonly media: MediaProjection[];
  readonly brand: string;
  readonly name: string;
  readonly price: PriceProjection;
  readonly size: SizeProjection;
  readonly color: ColorProjection;
  readonly status?: CheckoutItemStatus;
  readonly country: Country;
  readonly style?: Partial<ProductVariantStyle>;
  readonly onPress?: () => void;
}
const ProductVariant: FC<ProductVariantProps> = ({
  media,
  brand,
  name,
  price,
  size: sizeProjection,
  color,
  status,
  country,
  style: customStyle,
  onPress,
}) => {
  const uniqueText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.ITEM_UNIQUE });
  const colorLabel = useI18nMessage({ domain: DOMAIN, id: color.name, prefix: COLOR_I18N_PREFIX });
  const sizeChangeText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.PRODUCT_VARIANT_SIZE_CHANGE });

  const cdnImageUrl = useMediaImage();

  const style = useMemo(() => productVariantStyle(), []);

  return (
    <Pressable
      pointerEvents={onPress ? "auto" : "none"}
      style={style.container}
      testID="product-variant"
      onPress={onPress}
    >
      <View style={style.row}>
        <View>
          <LazyImage
            hiResSrc={cdnImageUrl({ url: media[0]?.url as string, width: IMAGE_WIDTH })}
            resizeMode="cover"
            src={cdnImageUrl({ url: media[0]?.url as string, width: IMAGE_WIDTH, dpi: 1 })}
            style={{ view: [style.media, customStyle?.image] }}
            testID="product-variant-media"
          />
        </View>

        <View style={style.descriptionContainer}>
          <View style={style.infoProductVariant}>
            <Text level={2} style={style.text} variant="detail">
              {brand}
            </Text>
            <Text level={2} variant="detail">
              {name}
            </Text>
            <Text level={2} style={style.text} variant="detail">
              {sizeProjection.unique ? uniqueText : size({ size: sizeProjection, country })} / {colorLabel}
            </Text>

            {status === CheckoutItemStatus.REPLACED && (
              <View style={style.sizeChange}>
                <Text level={2} variant="detail">
                  {sizeChangeText}
                </Text>
              </View>
            )}
          </View>

          <Price
            amount={price.amount}
            currency={price.currency}
            discountedPrice={price.discountedPrice}
            variant="detail"
          />
        </View>
      </View>

      {onPress && <Icon name="arrow_right" />}
    </Pressable>
  );
};

export type { ProductVariantStyle };
export { ProductVariant };
